<template>
  <div class="font-sans bg-gray-50 text-gray-800">
    <!-- Header -->
    <header class="bg-white border-b border-gray-200 fixed w-full z-10">
      <div class="flex items-center px-4 py-3">
        <!-- Logo on the left -->
        <div class="flex items-center">
          <router-link to="/" class="text-xl font-bold flex items-center text-custom-button-blue hover:text-custom-dark-blue">
            <img src="@/assets/images/logos/sidebarlogo.png" alt="Click AI" class="h-10">
          </router-link>
        </div>
      </div>
    </header>

    <div class="flex pt-14">
      <!-- Sidebar Navigation -->
      <div class="fixed top-14 left-0 w-64 h-screen bg-white overflow-y-auto border-r border-gray-200">
        <div class="px-4 py-4">
          <ul class="space-y-1">
            <li>
              <router-link to="/documentation" class="block py-2 px-3 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md font-medium cursor-pointer">
                <i class="fa-solid fa-book-open mr-2"></i> Introduction
              </router-link>
            </li>
            
            <li class="py-1">
              <div 
                @click="toggleSection('Leads')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage potential customers and track their interactions"
              >
                <span><i class="fa-solid fa-user-plus mr-2"></i> Leads</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Leads') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Leads')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/leads" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-custom-fields" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'leads-custom-fields' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get custom fields
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-new-lead" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'leads-new-lead' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create new lead
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-edit-lead" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'leads-edit-lead' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit lead
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-get-lead" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'leads-get-lead' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get lead by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-get-lead-phone" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'leads-get-lead-phone' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get leads by phone
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-add-call-log" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'leads-add-call-log' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Add call log
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Deals')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Track sales opportunities and manage deal pipeline"
              >
                <span><i class="fa-solid fa-handshake mr-2"></i> Deals</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Deals') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Deals')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/deals" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-custom-fields" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'deals-custom-fields' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get custom fields
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-new-deal" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'deals-new-deal' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create new deal
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-edit-deal" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'deals-edit-deal' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit deal
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-get-deal" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'deals-get-deal' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get deal by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-get-deal-phone" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'deals-get-deal-phone' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get deals by phone
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Customers')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage customer profiles and relationships"
              >
                <span><i class="fa-solid fa-users mr-2"></i> Customers</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Customers') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Customers')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/customers" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-custom-fields" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'customers-custom-fields' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get custom fields
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-create-customer" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'customers-create-customer' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create customer
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-get-customer" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'customers-get-customer' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get customer by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-get-customer-phone" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'customers-get-customer-phone' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get customers by phone
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-edit-customer" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'customers-edit-customer' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit customer
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-add-call-log" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'customers-add-call-log' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Add call log
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Products')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage product catalog and variations"
              >
                <span><i class="fa-solid fa-box mr-2"></i> Products</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Products') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Products')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/products" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-get-all" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'products-get-all' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get all products
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-get-product" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'products-get-product' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get product by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-create-product" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'products-create-product' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create product
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-edit-product" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'products-edit-product' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit product
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-add-variations" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'products-add-variations' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Add variations
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-delete-variation" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'products-delete-variation' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-red-100 text-red-800 px-1 rounded mr-2">DEL</span>
                    Delete variation
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Calendar')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Schedule meetings and manage appointments"
              >
                <span><i class="fa-solid fa-calendar mr-2"></i> Calendar</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Calendar') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Calendar')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/calendar" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-appointment-types" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-get-appointment-types' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get appointment types
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-appointment-users" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-get-appointment-users' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get appointment users
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-appointment-type-details" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-appointment-type-details' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get appointment details
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-user-working-hours" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-get-user-working-hours' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get user working hours
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-break-hours" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-get-break-hours' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get break hours
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-free-time-slot" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-get-free-time-slot' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get free time slots
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-double-free-time-slot" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-get-double-free-time-slot' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get double free slots
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-user-appointment-by-date" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-get-user-appointment-by-date' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get user appointments
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-schedule-meeting" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-schedule-meeting' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Schedule meeting
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-create-event-type" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-create-event-type' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create event type
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-edit-event-type" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'calendar-edit-event-type' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit event type
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Team')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage team members and user accounts"
              >
                <span><i class="fa-solid fa-users-gear mr-2"></i> Team</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Team') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Team')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/team" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/team-list" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'team-list' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get team members
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Tasks')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Create and manage tasks and assignments"
              >
                <span><i class="fa-solid fa-list-check mr-2"></i> Tasks</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Tasks') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Tasks')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/tasks" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/tasks-create-task" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'tasks-create-task' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create task
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/tasks-edit-task" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'tasks-edit-task' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit task
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/tasks-delete-task" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'tasks-delete-task' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-red-100 text-red-800 px-1 rounded mr-2">DEL</span>
                    Delete task
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Contacts')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage contact information and address book"
              >
                <span><i class="fa-solid fa-address-book mr-2"></i> Contacts</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Contacts') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Contacts')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/contacts" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/contacts-create-contact" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'contacts-create-contact' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create new contact
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/contacts-find-by-phone" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'contacts-find-by-phone' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Find by phone
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/contacts-find-by-name" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'contacts-find-by-name' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Find by name
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Chats')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Send messages and manage chat communications"
              >
                <span><i class="fa-solid fa-comments mr-2"></i> Chats</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Chats') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Chats')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/chats" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/chats-send-message" class="block py-1 text-sm cursor-pointer"
                    :class="$route.params.endpointId === 'chats-send-message' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Send message
                  </router-link>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>

      <!-- Main Content -->
      <div class="ml-64 flex-1 px-8 py-6">
        <div class="max-w-4xl" v-if="endpointData">
          <!-- Breadcrumb -->
          <div class="mb-8">
            <div class="flex items-center text-sm text-gray-500 mb-4">
              <span class="hover:text-custom-button-blue cursor-pointer">{{ endpointData.category }}</span>
              <i class="fas fa-chevron-right mx-2 text-gray-400 text-xs"></i>
              <span class="text-gray-700">{{ endpointData.title }}</span>
            </div>
            
            <!-- Title and method -->
            <h1 class="text-3xl font-bold text-gray-800 mb-4">{{ endpointData.title }}</h1>
            <div class="flex items-center mb-6">
              <span class="font-medium px-3 py-1 rounded-md text-sm mr-3"
                    :class="getMethodClass(endpointData.method)">
                {{ endpointData.method }}
              </span>
              <span class="font-mono text-gray-700">{{ endpointData.path }}</span>
            </div>
            <p class="text-gray-600 text-lg mb-6">{{ endpointData.description }}</p>
          </div>

          <!-- Headers Section -->
          <div v-if="endpointData.headers && endpointData.headers.length > 0" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">Header Parameters</h2>
            <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
              <div class="divide-y divide-gray-200">
                <div v-for="header in endpointData.headers" :key="header.name" class="p-4">
                  <div class="flex justify-between items-start mb-1">
                    <div>
                      <span class="font-medium text-gray-800">{{ header.name }}</span>
                      <span class="text-gray-500 text-sm ml-2">{{ header.type }}</span>
                      <span v-if="header.required" class="text-red-500 text-sm ml-1">required</span>
                    </div>
                  </div>
                  <p class="text-sm text-gray-600">{{ header.description }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Parameters Section -->
          <div v-if="endpointData.parameters && endpointData.parameters.length > 0" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">Body Parameters</h2>
            <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
              <div class="divide-y divide-gray-200">
                <div v-for="param in endpointData.parameters" :key="param.name" class="p-4">
                  <div class="flex justify-between items-start mb-1">
                    <div>
                      <span class="font-medium text-gray-800">{{ param.name }}</span>
                      <span class="text-gray-500 text-sm ml-2">{{ param.type }}</span>
                      <span v-if="param.required" class="text-red-500 text-sm ml-1">required</span>
                    </div>
                  </div>
                  <p class="text-sm text-gray-600">{{ param.description }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Responses Section -->
          <div v-if="endpointData.responses" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">Responses</h2>
            <div v-for="response in endpointData.responses" :key="response.code" class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
              <div class="flex items-center p-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50" 
                   @click="toggleResponse(response.code)">
                <div class="flex items-center">
                  <span class="w-2 h-2 rounded-full mr-2" :class="getStatusColor(response.code)"></span>
                  <span class="font-medium">{{ response.code }}</span>
                  <span class="text-gray-600 text-sm ml-2">{{ response.description }}</span>
                </div>
                <i class="fas fa-chevron-down text-gray-400 ml-auto transition-transform duration-300"
                   :class="{ 'rotate-180': openResponses.includes(response.code) }"></i>
              </div>
              <div v-show="openResponses.includes(response.code)" class="p-4 bg-gray-50">
                <div v-if="response.schema" class="mb-3">
                  <h3 class="text-sm font-medium text-gray-700 mb-1">Response Schema</h3>
                  <div class="bg-gray-100 p-3 rounded-md font-mono text-sm overflow-auto">
                    <pre>{{ response.schema }}</pre>
                  </div>
                </div>
                <div v-if="response.example">
                  <h3 class="text-sm font-medium text-gray-700 mb-1">Example Response</h3>
                  <div class="bg-gray-100 p-3 rounded-md font-mono text-sm overflow-auto">
                    <pre>{{ response.example }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Code Examples Section -->
          <div v-if="endpointData.codeExamples" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">Code Examples</h2>
            <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
              <div class="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 class="font-semibold">{{ endpointData.title }} Example</h3>
                <div class="relative">
                  <select v-model="selectedLanguage" class="pl-3 pr-3 py-1 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-1 focus:ring-custom-button-blue">
                    <option v-for="lang in availableLanguages" :key="lang" :value="lang">
                      {{ getLanguageLabel(lang) }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="bg-gray-800 text-gray-200 p-4 font-mono text-sm overflow-x-auto">
                <pre>{{ getCurrentCodeExample() }}</pre>
              </div>
              <div class="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <button @click="copyCodeToClipboard" 
                        class="px-3 py-1 rounded text-sm font-medium transition"
                        :class="copied ? 'bg-green-600 text-white' : 'bg-custom-button-blue text-white hover:bg-custom-dark-blue'">
                  <i :class="copied ? 'fas fa-check' : 'fas fa-copy'" class="mr-1"></i>
                  {{ copied ? 'Copied!' : 'Copy Code' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Try It Section -->
          <div class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">Try It Out</h2>
            <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
              <div class="p-4 border-b border-gray-200">
                <h3 class="font-semibold">Test {{ endpointData.title }}</h3>
                <p class="text-sm text-gray-600 mt-1">Fill in the required fields and send a request to test this endpoint.</p>
              </div>
              
              <div class="p-4">
                <!-- API Key Field -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    API Key <span class="text-red-500">*</span>
                  </label>
                  <input 
                    v-model="tryItForm.apiKey"
                    type="password" 
                    placeholder="your_api_key_here"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-button-blue focus:border-custom-button-blue"
                  />
                </div>

                <!-- Headers (if any besides Authorization) -->
                <div v-if="endpointData.headers && endpointData.headers.length > 1" class="mb-4">
                  <h4 class="text-sm font-medium text-gray-700 mb-2">Headers</h4>
                  <div v-for="header in endpointData.headers.filter(h => h.name !== 'Authorization')" :key="header.name" class="mb-2">
                    <label class="block text-xs text-gray-600 mb-1">
                      {{ header.name }}
                      <span v-if="header.required" class="text-red-500">*</span>
                    </label>
                    <input 
                      v-model="tryItForm.headers[header.name]"
                      type="text" 
                      :placeholder="header.description"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-button-blue focus:border-custom-button-blue text-sm"
                    />
                  </div>
                </div>

                <!-- Parameters -->
                <div v-if="endpointData.parameters && endpointData.parameters.length > 0" class="mb-4">
                  <h4 class="text-sm font-medium text-gray-700 mb-2">Request Body</h4>
                  <div v-for="param in endpointData.parameters" :key="param.name" class="mb-3">
                    <label class="block text-xs text-gray-600 mb-1">
                      {{ param.name }} ({{ param.type }})
                      <span v-if="param.required" class="text-red-500">*</span>
                    </label>
                    <div class="text-xs text-gray-500 mb-1">{{ param.description }}</div>
                    
                    <!-- Text/String inputs -->
                    <input 
                      v-if="param.type === 'string' || param.type === 'text'"
                      v-model="tryItForm.parameters[param.name]"
                      type="text" 
                      :placeholder="param.example || param.description"
                      :required="param.required"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-button-blue focus:border-custom-button-blue text-sm"
                    />
                    
                    <!-- Number inputs -->
                    <input 
                      v-else-if="param.type === 'number' || param.type === 'integer'"
                      v-model="tryItForm.parameters[param.name]"
                      type="number" 
                      :placeholder="param.example || param.description"
                      :required="param.required"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-button-blue focus:border-custom-button-blue text-sm"
                    />
                    
                    <!-- Boolean inputs -->
                    <div v-else-if="param.type === 'boolean'" class="flex items-center">
                      <input 
                        v-model="tryItForm.parameters[param.name]"
                        type="checkbox" 
                        class="mr-2"
                      />
                      <span class="text-sm text-gray-700">{{ param.description }}</span>
                    </div>
                    
                    <!-- Array inputs (like customFields) -->
                    <textarea 
                      v-else-if="param.type === 'array'"
                      v-model="tryItForm.parameters[param.name]"
                      :placeholder="param.example || 'Enter JSON array'"
                      :required="param.required"
                      rows="3"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-button-blue focus:border-custom-button-blue text-sm font-mono"
                    />
                    
                    <!-- Default text input for other types -->
                    <input 
                      v-else
                      v-model="tryItForm.parameters[param.name]"
                      type="text" 
                      :placeholder="param.example || param.description"
                      :required="param.required"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-button-blue focus:border-custom-button-blue text-sm"
                    />
                  </div>
                </div>

                <!-- Send Request Button -->
                <div class="flex justify-between items-center">
                  <button 
                    @click="sendTestRequest"
                    :disabled="!tryItForm.apiKey || loading"
                    class="px-4 py-2 bg-custom-button-blue text-white rounded-md hover:bg-custom-dark-blue transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i v-if="loading" class="fas fa-spinner fa-spin mr-2"></i>
                    <i v-else class="fas fa-play mr-2"></i>
                    {{ loading ? 'Sending...' : 'Send Request' }}
                  </button>
                  
                  <button 
                    @click="clearTryItForm"
                    class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition"
                  >
                    <i class="fas fa-eraser mr-2"></i>
                    Clear
                  </button>
                </div>
              </div>

              <!-- Response Section -->
              <div v-if="apiResponse" class="border-t border-gray-200">
                <div class="p-4 bg-gray-50">
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="font-medium text-gray-800">Response</h4>
                    <div class="flex items-center space-x-2">
                      <span class="text-sm" :class="apiResponse.status >= 200 && apiResponse.status < 300 ? 'text-green-600' : 'text-red-600'">
                        Status: {{ apiResponse.status }}
                      </span>
                      <button 
                        @click="copyResponseToClipboard"
                        class="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
                      >
                        <i class="fas fa-copy mr-1"></i>
                        Copy
                      </button>
                    </div>
                  </div>
                  
                  <!-- Response Headers -->
                  <div v-if="apiResponse.headers" class="mb-3">
                    <h5 class="text-xs font-medium text-gray-600 mb-1">Response Headers</h5>
                    <div class="bg-gray-100 p-2 rounded text-xs font-mono overflow-auto max-h-20">
                      <pre>{{ formatHeaders(apiResponse.headers) }}</pre>
                    </div>
                  </div>
                  
                  <!-- Response Body -->
                  <div>
                    <h5 class="text-xs font-medium text-gray-600 mb-1">Response Body</h5>
                    <div class="bg-gray-800 text-gray-200 p-3 rounded font-mono text-sm overflow-auto max-h-96">
                      <pre>{{ formatJson(apiResponse.data) }}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation Links -->
          <div class="flex justify-between items-center mb-10">
            <router-link v-if="previousEndpoint" :to="`/endpoint/${previousEndpoint.id}`" 
                         class="flex items-center text-custom-button-blue hover:text-custom-dark-blue cursor-pointer">
              <i class="fas fa-arrow-left mr-2"></i>
              <span>{{ previousEndpoint.name }}</span>
            </router-link>
            <div v-else></div>
            
            <router-link v-if="nextEndpoint" :to="`/endpoint/${nextEndpoint.id}`"
                         class="flex items-center text-custom-button-blue hover:text-custom-dark-blue cursor-pointer">
              <span>{{ nextEndpoint.name }}</span>
              <i class="fas fa-arrow-right ml-2"></i>
            </router-link>
            <div v-else></div>
          </div>

          <!-- Feedback Section -->
          <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-10">
            <div class="p-4">
              <h3 class="text-lg font-semibold mb-3">Was this helpful?</h3>
              <div class="flex space-x-2 mb-4">
                <button class="px-4 py-2 bg-custom-blue text-custom-dark-blue border border-custom-button-blue rounded-md hover:bg-custom-button-blue hover:text-white transition">
                  <i class="far fa-thumbs-up mr-2"></i> Yes
                </button>
                <button class="px-4 py-2 bg-gray-50 text-gray-700 border border-gray-200 rounded-md hover:bg-gray-100 transition">
                  <i class="far fa-thumbs-down mr-2"></i> No
                </button>
              </div>
              <div>
                <textarea class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-button-blue" 
                          rows="3" placeholder="Leave feedback (optional)"></textarea>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Loading state -->
        <div v-else class="ml-64 flex-1 px-8 py-6 flex items-center justify-center">
          <div class="text-center">
            <i class="fas fa-spinner fa-spin text-custom-button-blue text-3xl mb-4"></i>
            <p class="text-gray-600">Loading endpoint details...</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { categories, getEndpointData, getAllEndpoints } from '@/services/documentationParser'

const route = useRoute()
const router = useRouter()

// Reactive data
const endpointData = ref(null)
const selectedLanguage = ref('javascript')
const openCategories = ref([])
const openSections = ref([])
const openResponses = ref(['200'])
const copied = ref(false)

// Try It functionality
const loading = ref(false)
const apiResponse = ref(null)
const tryItForm = ref({
  apiKey: '',
  headers: {},
  parameters: {}
})

const availableLanguages = computed(() => {
  if (!endpointData.value?.codeExamples) return []
  return Object.keys(endpointData.value.codeExamples)
})

const previousEndpoint = computed(() => {
  if (!endpointData.value) return null
  
  const allEndpoints = getAllEndpoints()
  const currentIndex = allEndpoints.findIndex(ep => ep.id === endpointData.value.id)
  
  return currentIndex > 0 ? allEndpoints[currentIndex - 1] : null
})

const nextEndpoint = computed(() => {
  if (!endpointData.value) return null
  
  const allEndpoints = getAllEndpoints()
  const currentIndex = allEndpoints.findIndex(ep => ep.id === endpointData.value.id)
  
  return currentIndex < allEndpoints.length - 1 ? allEndpoints[currentIndex + 1] : null
})

// Methods

const isCurrentCategory = (categoryId) => {
  return endpointData.value?.categoryId === categoryId
}

const isCurrentEndpoint = (endpointId) => {
  return endpointData.value?.id === endpointId
}

const toggleCategory = (categoryId) => {
  if (openCategories.value.includes(categoryId)) {
    openCategories.value = openCategories.value.filter(id => id !== categoryId)
  } else {
    openCategories.value.push(categoryId)
  }
}

const toggleSection = (sectionName) => {
  const index = openSections.value.indexOf(sectionName)
  if (index > -1) {
    // אם התפריט כבר פתוח, סוגרים אותו
    openSections.value = []
  } else {
    // אם התפריט סגור, סוגרים את כל האחרים ופותחים רק אותו
    openSections.value = [sectionName]
  }
}

const toggleResponse = (code) => {
  if (openResponses.value.includes(code)) {
    openResponses.value = openResponses.value.filter(c => c !== code)
  } else {
    openResponses.value.push(code)
  }
}

const getMethodClass = (method) => {
  const classes = {
    GET: 'bg-green-100 text-green-800',
    POST: 'bg-blue-100 text-blue-800',
    PUT: 'bg-orange-100 text-orange-800',
    DELETE: 'bg-red-100 text-red-800'
  }
  return classes[method] || 'bg-gray-100 text-gray-800'
}

const getStatusColor = (code) => {
  if (code.startsWith('2')) return 'bg-green-500'
  if (code.startsWith('3')) return 'bg-blue-500'
  if (code.startsWith('4')) return 'bg-orange-500'
  if (code.startsWith('5')) return 'bg-red-500'
  return 'bg-gray-500'
}

const getLanguageLabel = (lang) => {
  const labels = {
    javascript: 'JavaScript',
    python: 'Python',
    curl: 'cURL (Windows)',
    curlLinux: 'cURL (Linux/Mac)',
    php: 'PHP',
    csharp: 'C#',
    ruby: 'Ruby',
    java: 'Java'
  }
  return labels[lang] || lang
}

const getCurrentCodeExample = () => {
  if (!endpointData.value?.codeExamples) return ''
  return endpointData.value.codeExamples[selectedLanguage.value] || ''
}

const copyCodeToClipboard = async () => {
  try {
    const code = getCurrentCodeExample()
    await navigator.clipboard.writeText(code)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = getCurrentCodeExample()
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  }
}

// Try It methods
const sendTestRequest = async () => {
  if (!tryItForm.value.apiKey) {
    alert('Please enter your API key')
    return
  }

  loading.value = true
  apiResponse.value = null

  try {
    // Build headers
    const headers = {
      'Authorization': `Bearer ${tryItForm.value.apiKey}`,
      'Content-Type': 'application/json',
      ...tryItForm.value.headers
    }

    // Build request options
    const requestOptions = {
      method: endpointData.value.method,
      headers: headers
    }

    // Add body for non-GET requests
    if (endpointData.value.method !== 'GET' && Object.keys(tryItForm.value.parameters).length > 0) {
      // Process parameters
      const processedParams = {}
      for (const [key, value] of Object.entries(tryItForm.value.parameters)) {
        if (value !== '' && value !== null && value !== undefined) {
          // Try to parse JSON for array/object fields
          if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
            try {
              processedParams[key] = JSON.parse(value)
            } catch (e) {
              processedParams[key] = value
            }
          } else {
            processedParams[key] = value
          }
        }
      }
      requestOptions.body = JSON.stringify(processedParams)
    }

    // Make the request
    const response = await fetch(`https://dev.clickai.live${endpointData.value.path}`, requestOptions)
    
    // Get response data
    let responseData
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json()
    } else {
      responseData = await response.text()
    }

    // Store response
    apiResponse.value = {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
      data: responseData
    }

  } catch (error) {
    apiResponse.value = {
      status: 0,
      statusText: 'Network Error',
      headers: {},
      data: {
        error: error.message || 'Failed to connect to the API. Please check your network connection.'
      }
    }
  } finally {
    loading.value = false
  }
}

const clearTryItForm = () => {
  tryItForm.value = {
    apiKey: '',
    headers: {},
    parameters: {}
  }
  apiResponse.value = null
}

const copyResponseToClipboard = async () => {
  if (!apiResponse.value) return
  
  try {
    const responseText = formatJson(apiResponse.value.data)
    await navigator.clipboard.writeText(responseText)
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = formatJson(apiResponse.value.data)
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
}

const formatJson = (data) => {
  if (typeof data === 'string') {
    try {
      return JSON.stringify(JSON.parse(data), null, 2)
    } catch (e) {
      return data
    }
  }
  return JSON.stringify(data, null, 2)
}

const formatHeaders = (headers) => {
  return Object.entries(headers)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n')
}

// Load endpoint data
const loadEndpointData = async (endpointId) => {
  try {
    const data = getEndpointData(endpointId)
    endpointData.value = data
    
    // פתיחת הקטגוריה הנכונה בהתבסס על ה-endpoint
    if (data && endpointId) {
      let categoryName = ''
      
      if (endpointId.startsWith('leads-')) {
        categoryName = 'Leads'
      } else if (endpointId.startsWith('deals-')) {
        categoryName = 'Deals'
      } else if (endpointId.startsWith('customers-')) {
        categoryName = 'Customers'
      } else if (endpointId.startsWith('products-')) {
        categoryName = 'Products'
      } else if (endpointId.startsWith('calendar-')) {
        categoryName = 'Calendar'
      } else if (endpointId.startsWith('team-')) {
        categoryName = 'Team'
      } else if (endpointId.startsWith('tasks-')) {
        categoryName = 'Tasks'
      } else if (endpointId.startsWith('contacts-')) {
        categoryName = 'Contacts'
      } else if (endpointId.startsWith('chats-')) {
        categoryName = 'Chats'
      }
      
      if (categoryName && !openSections.value.includes(categoryName)) {
        openSections.value = [categoryName]
      }
    }
    
    if (!data) {
      console.error('Endpoint not found:', endpointId)
      router.push('/documentation')
    }
  } catch (error) {
    console.error('Error loading endpoint data:', error)
    router.push('/documentation')
  }
}

// Watchers
watch(() => route.params.endpointId, (newId) => {
  if (newId) {
    loadEndpointData(newId)
    // Clear Try It form when switching endpoints
    clearTryItForm()
  }
}, { immediate: true })

// Initialize Try It form when endpoint data loads
watch(endpointData, (newData) => {
  if (newData) {
    // Pre-fill form with default values from examples
    if (newData.parameters) {
      const defaultParams = {}
      newData.parameters.forEach(param => {
        if (param.example) {
          defaultParams[param.name] = param.example
        }
      })
      tryItForm.value.parameters = defaultParams
    }
    
    // Pre-fill headers
    if (newData.headers) {
      const defaultHeaders = {}
      newData.headers.forEach(header => {
        if (header.name !== 'Authorization' && header.description) {
          defaultHeaders[header.name] = header.description.includes('application/json') ? 'application/json' : ''
        }
      })
      tryItForm.value.headers = defaultHeaders
    }
  }
})

// Lifecycle
onMounted(() => {
  const endpointId = route.params.endpointId
  if (endpointId) {
    loadEndpointData(endpointId)
  }
})
</script>

<style scoped>
.rotate-90 {
  transform: rotate(90deg);
}

.rotate-180 {
  transform: rotate(180deg);
}
</style> 